import { useEffect, useRef, useState } from 'react';
import {
  makeStyles, tokens, Title1, Title3, Button, Spinner, Text, Badge,
  ProgressBar, Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
} from '@fluentui/react-components';
import { apiGet, apiUpload } from '../api/client';
import type {
  BatchSummary, BatchStatus, DocumentStatus, UploadBatchResponse,
} from '../api/types';

const useStyles = makeStyles({
  section: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '16px 20px',
    marginBottom: '16px',
  },
  uploadRow: { display: 'flex', gap: '12px', alignItems: 'center', marginTop: '8px' },
  err: { color: tokens.colorPaletteRedForeground1 },
  progressWrap: {
    marginTop: '12px',
    padding: '12px 14px',
    background: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  fileName: {
    maxWidth: '320px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  smallText: { fontSize: '12px', color: tokens.colorNeutralForeground3 },
});

const DOC_LABEL: Record<DocumentStatus, string> = { 0: 'Uploaded', 1: 'Processing', 2: 'Ready', 3: 'Failed' };
const DOC_COLOR: Record<DocumentStatus, 'informative' | 'brand' | 'success' | 'danger'> = {
  0: 'informative', 1: 'brand', 2: 'success', 3: 'danger',
};

const BATCH_LABEL: Record<BatchStatus, string> = {
  0: 'Queued', 1: 'Extracting', 2: 'Processing',
  3: 'Completed', 4: 'Completed with errors', 5: 'Failed',
};
const BATCH_COLOR: Record<BatchStatus, 'informative' | 'brand' | 'success' | 'warning' | 'danger'> = {
  0: 'informative', 1: 'brand', 2: 'brand',
  3: 'success', 4: 'warning', 5: 'danger',
};

function isTerminal(s: BatchStatus): boolean {
  return s === 3 || s === 4 || s === 5;
}

function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Batches() {
  const s = useStyles();
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [uploadInfo, setUploadInfo] = useState<UploadBatchResponse | null>(null);
  const [activeBatch, setActiveBatch] = useState<BatchSummary | null>(null);
  const [history, setHistory] = useState<BatchSummary[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  async function refreshHistory() {
    try {
      const list = await apiGet<BatchSummary[]>('/api/batches');
      setHistory(list);
    } catch { /* the panel is decorative */ }
  }

  useEffect(() => { refreshHistory(); }, []);

  // Poll the active batch every 2 s until it terminates.
  useEffect(() => {
    if (!activeBatch) return;
    if (isTerminal(activeBatch.status)) { refreshHistory(); return; }

    let cancelled = false;
    const t = setInterval(async () => {
      try {
        const b = await apiGet<BatchSummary>(`/api/batches/${activeBatch.id}`);
        if (cancelled) return;
        setActiveBatch(b);
        if (isTerminal(b.status)) {
          clearInterval(t);
          refreshHistory();
        }
      } catch { /* keep polling — transient errors are fine */ }
    }, 2000);
    return () => { cancelled = true; clearInterval(t); };
  }, [activeBatch?.id, activeBatch?.status]);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadErr(null);
    setUploadInfo(null);
    setActiveBatch(null);
    setUploading(true);
    try {
      const r = await apiUpload<UploadBatchResponse>('/api/batches/upload', file);
      setUploadInfo(r);
      // Seed the active batch so polling starts. Fields we don't know yet are placeholders.
      setActiveBatch({
        id: r.batchId, fileName: r.fileName, sizeBytes: file.size,
        totalFiles: r.acceptedFiles, processedFiles: 0, failedFiles: 0,
        status: r.status, errorMessage: null,
        uploadedAt: new Date().toISOString(), completedAt: null,
        documents: [],
      });
    } catch (err) {
      setUploadErr(`Upload failed: ${(err as Error).message}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const done = activeBatch ? activeBatch.processedFiles + activeBatch.failedFiles : 0;
  const total = activeBatch?.totalFiles ?? 0;
  const pct = total > 0 ? done / total : 0;

  return (
    <div>
      <Title1 as="h1" style={{ marginBottom: 16 }}>Batch upload — zip of PDFs</Title1>

      <div className={s.section}>
        <Title3 as="h2">Upload a .zip archive</Title3>
        <Text size={200} block>
          The archive must contain PDF files (up to 100 files, 250 MB total).
          Non-PDF entries are skipped. Each PDF is chunked and embedded
          in parallel — status updates below.
        </Text>
        <div className={s.uploadRow}>
          <input
            ref={fileRef}
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed"
            onChange={onFile}
            disabled={uploading}
          />
          {uploading && <Spinner size="tiny" label="Uploading…" />}
        </div>
        {uploadErr && <Text className={s.err} style={{ marginTop: 8, display: 'block' }}>{uploadErr}</Text>}
        {uploadInfo && uploadInfo.skippedEntries.length > 0 && (
          <Text size={200} style={{ marginTop: 8, display: 'block', color: tokens.colorPaletteDarkOrangeForeground1 }}>
            Skipped {uploadInfo.skippedEntries.length} non-PDF entr{uploadInfo.skippedEntries.length === 1 ? 'y' : 'ies'}: {uploadInfo.skippedEntries.slice(0, 5).join(', ')}
            {uploadInfo.skippedEntries.length > 5 && ` and ${uploadInfo.skippedEntries.length - 5} more`}
          </Text>
        )}

        {activeBatch && (
          <div className={s.progressWrap}>
            <div className={s.progressHeader}>
              <div>
                <div style={{ fontWeight: 600 }}>{activeBatch.fileName}</div>
                <div className={s.smallText}>
                  {done} of {total} file{total === 1 ? '' : 's'} finished
                  {activeBatch.failedFiles > 0 && ` — ${activeBatch.failedFiles} failed`}
                </div>
              </div>
              <Badge color={BATCH_COLOR[activeBatch.status]} appearance="filled">
                {BATCH_LABEL[activeBatch.status]}
              </Badge>
            </div>
            <ProgressBar value={pct} max={1} thickness="large" />

            {activeBatch.documents.length > 0 && (
              <Table size="small" style={{ marginTop: 16 }}>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>File</TableHeaderCell>
                    <TableHeaderCell>Size</TableHeaderCell>
                    <TableHeaderCell>Pages</TableHeaderCell>
                    <TableHeaderCell>Chunks</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeBatch.documents.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>
                        <div className={s.fileName} title={d.fileName}>{d.fileName}</div>
                        {d.errorMessage && (
                          <div className={s.smallText} style={{ color: tokens.colorPaletteRedForeground1 }}>
                            {d.errorMessage}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{humanBytes(d.sizeBytes)}</TableCell>
                      <TableCell>{d.pageCount || '—'}</TableCell>
                      <TableCell>{d.chunkCount || '—'}</TableCell>
                      <TableCell>
                        <Badge color={DOC_COLOR[d.status]}>{DOC_LABEL[d.status]}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </div>

      <div className={s.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Title3 as="h2">Recent batches</Title3>
          <Button size="small" appearance="subtle" onClick={refreshHistory}>Refresh</Button>
        </div>
        {history.length === 0 && <Text size={200}>No batches uploaded yet.</Text>}
        {history.length > 0 && (
          <Table size="small" style={{ marginTop: 12 }}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Zip</TableHeaderCell>
                <TableHeaderCell>Size</TableHeaderCell>
                <TableHeaderCell>Progress</TableHeaderCell>
                <TableHeaderCell>Uploaded</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((b) => (
                <TableRow
                  key={b.id}
                  onClick={() => setActiveBatch(b)}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <div className={s.fileName} title={b.fileName}>{b.fileName}</div>
                  </TableCell>
                  <TableCell>{humanBytes(b.sizeBytes)}</TableCell>
                  <TableCell>
                    {b.processedFiles + b.failedFiles} / {b.totalFiles}
                    {b.failedFiles > 0 && (
                      <span style={{ color: tokens.colorPaletteRedForeground1, marginLeft: 6 }}>
                        ({b.failedFiles} failed)
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{new Date(b.uploadedAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge color={BATCH_COLOR[b.status]}>{BATCH_LABEL[b.status]}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
